import config from "../../config";
import getClientTimeZone from "../getClientTimeZone";
import DEFAULT_DRAWN_OBJECTS_OPTIONS from "../TvChart/defaultDrawnObjectsOptions";
import getMainSeriesInterval from "../TvChart/getMainSeriesInterval";
import EDITORIAL_PERMISSIONS_NAMES from "../../redux/modules/auth/userPermissions/editorialPermissions";
import { PLAN_PERMISSIONS_NAMES } from "../../redux/modules/auth/userPermissions/selector";
import buildTvChartBridgeChartingLibraryTranslations from "./buildTvChartBridgeChartingLibraryTranslations";

const DISABLED_FEATURES = [
  "header_symbol_search",
  "header_interval_dialog_button",
  "header_compare",
  "items_favoriting",
  "display_market_status",
  "link_to_tradingview",
  "header_fullscreen_button"
];

const ENABLED_FEATURES = [
  "side_toolbar_in_fullscreen_mode",
  "keep_left_toolbar_visible_on_small_screens",
  "dont_show_boolean_study_arguments",
  "hide_last_na_study_output",
  "move_logo_to_main_pane",
  "charting_library_single_symbol_request"
];

export const OVERRIDES = {
  "paneProperties.background": "#FFFFFF",
  "mainSeriesProperties.candleStyle.upColor": "#6CA584",
  "mainSeriesProperties.candleStyle.downColor": "#D65748",
  "mainSeriesProperties.candleStyle.drawWick": true,
  "mainSeriesProperties.candleStyle.drawBorder": true,
  "mainSeriesProperties.candleStyle.borderColor": "#378658",
  "mainSeriesProperties.candleStyle.borderUpColor": "#235438",
  "mainSeriesProperties.candleStyle.borderDownColor": "#5A1C16",
  "mainSeriesProperties.candleStyle.wickUpColor": "#737375",
  "mainSeriesProperties.candleStyle.wickDownColor": "#737375",
  "mainSeriesProperties.candleStyle.barColorsOnPrevClose": false,

  // Heikin-Ashi (parity with legacy TVChart)
  "mainSeriesProperties.haStyle.upColor": "#6CA584",
  "mainSeriesProperties.haStyle.downColor": "#D65748",
  "mainSeriesProperties.haStyle.drawWick": true,
  "mainSeriesProperties.haStyle.drawBorder": true,
  "mainSeriesProperties.haStyle.borderColor": "#378658",
  "mainSeriesProperties.haStyle.borderUpColor": "#235438",
  "mainSeriesProperties.haStyle.borderDownColor": "#5A1C16",
  "mainSeriesProperties.haStyle.wickColor": "#737375",
  "mainSeriesProperties.haStyle.barColorsOnPrevClose": false
};

export const TV_CHART_BRIDGE_TRANSLATION_KEYS = [
  "Templates",
  "Backups",
  "Analytics",
  "Chart templates",
  "Chart backups",
  "Show objects options",
  "Add template",
  "Add analysis",
  "Template name",
  "Analysis name",
  "Analysis description",
  "shared_analyzes",
  "your_analyses",
  "save_and_publish_analysis",
  "confirm_publish_analysis",
  "confirm_unpublish_analysis",
  "tooltip-publish-analysis",
  "tooltip-unpublish-analysis",
  "tooltip-edit-template",
  "tooltip-overwrite-template",
  "tooltip-apply-template",
  "tooltip-remove-template",
  "tooltip-edit-analysis",
  "tooltip-apply-analysis",
  "tooltip-remove-analysis",
  "tooltip-apply-template-changes",
  "tooltip-discard-template-changes",
  "Cancel",
  "Apply",
  "tooltip-restore-backup",
  "visibility_published",
  "visibility_private",
  "Default template",
  "Is default",
  "Applying backup failed",
  "This field is required",
  "Price Alerts",
  "Add price alert",
  "Select stock",
  "Fullscreen",
  ...DEFAULT_DRAWN_OBJECTS_OPTIONS.map(option => option.label)
];

export function getDaysSinceStartOfTheYear(now = new Date()) {
  const start = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now - start) / (1000 * 60 * 60 * 24));
}

export function getTvChartBridgeTimeFrames(now = new Date()) {
  return [
    { text: "1m", resolution: "1D", title: "1M" },
    { text: "6m", resolution: "1D", title: "6M" },
    { text: "1y", resolution: "1D", title: "1Y" },
    { text: "2y", resolution: "1D", title: "2Y" },
    { text: "3y", resolution: "1D", title: "3Y" },
    { text: "5y", resolution: "1W", title: "5Y" },
    {
      text: `${getDaysSinceStartOfTheYear(now)}d`,
      resolution: "1D",
      title: "YTD"
    },
    { text: "500m", resolution: "1W", title: "All" }
  ];
}

export function getTvChartBridgeIntervals(stock) {
  let intervals = config.tradingViewOptions.defaultMarketsIntervals;

  const specificMarketIntervals = config.tradingViewOptions.marketsIntervals.find(
    marketInterval =>
      marketInterval.markets.findIndex(
        market => market === stock.market.toLowerCase()
      ) !== -1
  );

  if (specificMarketIntervals) {
    intervals = specificMarketIntervals.intervals;
  }

  return intervals;
}

// Full locale map for the iframe `t()` helper. Overlay copy (markers, alerts)
// looks up keys at draw time; a whitelist cannot keep up with those strings.
function getPhraseMap(phrases) {
  if (!phrases || typeof phrases !== "object") {
    return {};
  }

  return phrases;
}

export function buildTvChartBridgeTranslations(translate, phrases = null) {
  const translations = {};

  TV_CHART_BRIDGE_TRANSLATION_KEYS.forEach(key => {
    translations[key] = typeof translate === "function" ? translate(key) : key;
  });

  return {
    ...translations,
    ...buildTvChartBridgeChartingLibraryTranslations(phrases),
    ...getPhraseMap(phrases)
  };
}

export function buildTvChartBridgePermissions(hasPermission, userId = null) {
  const check =
    typeof hasPermission === "function" ? hasPermission : () => false;

  const hasPremium = !!check([PLAN_PERMISSIONS_NAMES.TRADING_VIEW_CHART]);
  const canPublishAnalyses = !!check([
    EDITORIAL_PERMISSIONS_NAMES.CAN_PUBLISH_ANALYSES
  ]);

  const drawnObjectsAccess = {};
  DEFAULT_DRAWN_OBJECTS_OPTIONS.forEach(option => {
    drawnObjectsAccess[option.key] = check(option.permissions || []);
  });

  return {
    hasPremium,
    drawnObjectsAccess,
    canPublishAnalyses,
    userId: userId ?? null
  };
}

export function getTvChartBridgeLogoImageUrl({
  isNative = false,
  logoImageUrl
} = {}) {
  if (logoImageUrl) {
    return logoImageUrl;
  }

  if (!config.tradingViewOptions?.logo) {
    return null;
  }

  // Native extract dir (same folder as tvchartbridge.html); web public path.
  return isNative ? "./squaber-logo-tv.png" : "/images/squaber-logo-tv.png";
}

/**
 * Build init payload for tvchartbridge.html (no datafeed — proxy lives in HTML).
 */
export default function buildTvChartBridgeInitPayload({
  stock,
  locale,
  libraryPath,
  widgetScriptUrl,
  overlaysScriptUrl,
  savedChart,
  interval,
  drawnObjectsOptions = DEFAULT_DRAWN_OBJECTS_OPTIONS,
  backups = [],
  permissions = { hasPremium: false, drawnObjectsAccess: {} },
  translations = {},
  translate,
  phrases = null,
  logoImageUrl = null,
  isNative = false,
  showStocksListButton = false,
  showFullscreenButton = false,
  showLeadingFullscreenButton = false
}) {
  const mergedTranslations =
    Object.keys(translations || {}).length > 0
      ? {
          ...buildTvChartBridgeChartingLibraryTranslations(phrases),
          ...translations,
          ...getPhraseMap(phrases)
        }
      : buildTvChartBridgeTranslations(translate, phrases);

  const resolvedLogoUrl = getTvChartBridgeLogoImageUrl({
    isNative,
    logoImageUrl
  });

  return {
    symbol: stock.ticker,
    interval: interval || getMainSeriesInterval(savedChart) || "D",
    locale,
    libraryPath,
    widgetScriptUrl,
    overlaysScriptUrl: overlaysScriptUrl || "./tvchartbridge-overlays.js",
    savedChart: savedChart || null,
    logoImageUrl: resolvedLogoUrl,
    showStocksListButton: !!showStocksListButton,
    showFullscreenButton: !!showFullscreenButton,
    showLeadingFullscreenButton: !!showLeadingFullscreenButton,
    drawnObjectsOptionsSchema: DEFAULT_DRAWN_OBJECTS_OPTIONS.map(option => ({
      key: option.key,
      label: option.label,
      permissions: option.permissions || [],
      enabled_intervals: option.enabled_intervals || null
    })),
    drawnObjectsOptions: (drawnObjectsOptions || []).map(option => ({
      key: option.key,
      value: option.value
    })),
    backups: backups || [],
    permissions: permissions || buildTvChartBridgePermissions(() => false),
    translations: mergedTranslations,
    widgetOptions: {
      allow_symbol_change: false,
      timezone: getClientTimeZone(),
      loading_screen: { backgroundColor: "#FFFFFF" },
      toolbar_bg: "#FFFFFF",
      autosize: true,
      disabled_features: DISABLED_FEATURES,
      enabled_features: ENABLED_FEATURES,
      overrides: OVERRIDES,
      time_frames: getTvChartBridgeTimeFrames()
    }
  };
}
